import React, { useState, useEffect } from 'react';
import { getAllClients } from '../utils/apiService';
import StatisticsOverview from '../components/statistics/StatisticsOverview';
import DistributionPieChart from '../components/statistics/DistributionPieChart';
import VersionChart from '../components/statistics/VersionChart';
import VersionDetailsTable from '../components/statistics/VersionDetailsTable';
import IssuesSection from '../components/statistics/IssuesSection';

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    mariadb: {},
    apache: {},
    nodejs: {},
    patientApp: {},
    managerFront: {},
    marshaller: {},
    marshallerInterop: {},
    serverTypes: {},
    environments: {},
    remoteMaintenance: { enabled: 0, disabled: 0 },
    totalServers: 0,
    totalClients: 0,
    probesNotDeployed: [],
    dbNotOptimized: []
  });

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const allClients = await getAllClients();

      const stats = {
        mariadb: {},
        apache: {},
        nodejs: {},
        patientApp: {},
        managerFront: {},
        marshaller: {},
        marshallerInterop: {},
        serverTypes: {},
        environments: {},
        remoteMaintenance: { enabled: 0, disabled: 0 },
        totalServers: 0,
        totalClients: allClients.length,
        probesNotDeployed: [],
        dbNotOptimized: []
      };

      // Parcourir tous les clients et leurs serveurs
      allClients.forEach(client => {
        // Statistiques de maintenance à distance
        if (client.remoteMaintenance) {
          stats.remoteMaintenance.enabled++;
        } else {
          stats.remoteMaintenance.disabled++;
        }

        // Statistiques d'environnement
        const env = client.environment || 'Non défini';
        stats.environments[env] = (stats.environments[env] || 0) + 1;

        // Parcourir les serveurs
        if (client.servers && Array.isArray(client.servers)) {
          client.servers.forEach(server => {
            stats.totalServers++;

            // Type de serveur
            const serverType = server.type || 'Non défini';
            stats.serverTypes[serverType] = (stats.serverTypes[serverType] || 0) + 1;

            // Versions MariaDB
            if (server.mariadbVersion) {
              const version = server.mariadbVersion;
              stats.mariadb[version] = (stats.mariadb[version] || 0) + 1;
            }

            // Versions Apache
            if (server.apacheVersion) {
              const version = server.apacheVersion;
              stats.apache[version] = (stats.apache[version] || 0) + 1;
            }

            // Versions Node.js
            if (server.nodejsVersion) {
              const version = server.nodejsVersion;
              stats.nodejs[version] = (stats.nodejs[version] || 0) + 1;
            }

            // Versions des applications
            if (server.gitPatientAppVersion) {
              const version = server.gitPatientAppVersion;
              stats.patientApp[version] = (stats.patientApp[version] || 0) + 1;
            }

            if (server.gitManagerFrontAppVersion) {
              const version = server.gitManagerFrontAppVersion;
              stats.managerFront[version] = (stats.managerFront[version] || 0) + 1;
            }

            if (server.gitMarshallerVersion) {
              const version = server.gitMarshallerVersion;
              stats.marshaller[version] = (stats.marshaller[version] || 0) + 1;
            }

            if (server.gitMarshallerInteropVersion) {
              const version = server.gitMarshallerInteropVersion;
              stats.marshallerInterop[version] = (stats.marshallerInterop[version] || 0) + 1;
            }

            // Vérifier les sondes non déployées
            if (server.probeDeployed === false && server.type !== 'Serveur base de données') {
              stats.probesNotDeployed.push({
                clientName: client.name,
                environment: client.environment,
                serverName: server.name,
                serverType: server.type
              });
            }

            // Vérifier les BDD non optimisées
            if (server.dbOptimized === false && server.mariadbVersion) {
              stats.dbNotOptimized.push({
                clientName: client.name,
                environment: client.environment,
                serverName: server.name,
                mariadbVersion: server.mariadbVersion
              });
            }
          });
        }
      });

      setStatistics(stats);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Statistiques du Parc</h1>
        <button 
          onClick={loadStatistics}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Vue d'ensemble */}
      <StatisticsOverview statistics={statistics} />

      {/* Graphiques de distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DistributionPieChart 
          data={statistics.serverTypes} 
          title="Répartition par Type de Serveur"
        />
        
        <DistributionPieChart 
          data={statistics.environments} 
          title="Répartition par Environnement"
        />
      </div>

      {/* Graphiques des versions - Infrastructure */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">Infrastructure</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <VersionChart 
            data={statistics.mariadb} 
            title="Versions MariaDB"
            color="#3B82F6"
            showPercentage={true}
          />
          
          <VersionChart 
            data={statistics.apache} 
            title="Versions Apache"
            color="#10B981"
            showPercentage={true}
          />
          
          <VersionChart 
            data={statistics.nodejs} 
            title="Versions Node.js"
            color="#F59E0B"
            showPercentage={true}
          />
        </div>
      </div>

      {/* Graphiques des versions - Applications */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">Applications</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <VersionChart 
            data={statistics.patientApp} 
            title="Versions Patient App"
            color="#EF4444"
            showPercentage={true}
          />
          
          <VersionChart 
            data={statistics.managerFront} 
            title="Versions Manager Front"
            color="#8B5CF6"
            showPercentage={true}
          />
          
          <VersionChart 
            data={statistics.marshaller} 
            title="Versions Marshaller"
            color="#EC4899"
            showPercentage={true}
          />
          
          <VersionChart 
            data={statistics.marshallerInterop} 
            title="Versions Marshaller Interop"
            color="#6366F1"
            showPercentage={true}
          />
        </div>
      </div>

      {/* Tableau détaillé */}
      <VersionDetailsTable statistics={statistics} />
      
      {/* Section des problèmes */}
      <IssuesSection statistics={statistics} />
    </div>
  );
};

export default Statistics;